import { LoadState } from "@redotech/react-util/load";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { DiscountType } from "@redotech/redo-model/discount";
import { ProductImageAspectRatio } from "@redotech/redo-model/image";
import { RedoRecommendedProduct } from "@redotech/redo-model/redo-recommended-product-schema";
import { Trackable } from "@redotech/redo-model/trackable";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as classNames from "classnames";
import { memo, useContext, useEffect, useState } from "react";
import { DiscountResponse } from "../../../utils/discount-code";
import * as trackingCss from "../order-tracking.module.css";
import { ProductDeliveryRange } from "../product-delivery-range";
import { calculateDiscountPrice, validDiscount } from "./discount-util";

export const RecommendedProductComponent = memo(
  function RecommendedProductComponent({
    settings,
    discountResponse,
    product,
    index,
    collectionsLoad,
    trackable,
    upsellRedirect,
    imageAspectRatio,
  }: {
    settings: ReturnAppSettings;
    product: RedoRecommendedProduct;
    index: number;
    discountResponse?: DiscountResponse;
    collectionsLoad: LoadState<any>;
    trackable: Trackable;
    upsellRedirect: {
      logUpsellAndRedirect: (selectedVariant: {
        id: string;
        handle: string;
      }) => Promise<void>;
    };
    imageAspectRatio?: number;
  }) {
    const { formatCurrency } = useContext(CurrencyContext);
    const [isHovered, setIsHovered] = useState(false);

    const getImageUrl = (product: RedoRecommendedProduct) => {
      const imageUrl =
        isHovered &&
        product?.altImage?.url &&
        settings?.orderTracking?.upsellProducts?.hoverSecondImage &&
        product?.altImage?.url
          ? new URL(product?.altImage?.url)
          : product?.featuredImage?.url
            ? new URL(product?.featuredImage?.url)
            : null;
      if (!imageUrl) {
        return null;
      }

      imageUrl.searchParams.append("width", "144");
      return imageUrl.toString();
    };
    const getAltText = (product: RedoRecommendedProduct) => {
      return isHovered &&
        product?.altImage?.altText &&
        settings?.orderTracking?.upsellProducts?.hoverSecondImage
        ? product?.altImage?.altText
        : product?.featuredImage?.altText;
    };

    const options = product.options.reduce(
      (acc, option) => {
        // Products with no variants have a "Title" option, we want to skip these
        if (option.name === "Title") return acc;

        acc[option.name] = option.values;
        return acc;
      },
      {} as Record<string, string[]>,
    );

    const [selectedOptions, setSelectedOptions] = useState<
      { name: string; value: string }[] | undefined
    >(undefined);

    // select the default variant
    useEffect(() => {
      setSelectedOptions(
        // @ts-expect-error: strict-null-checks
        product.options.map((option) => ({
          name: option.name,
          value: option.values[0],
        })),
      );
    }, [product.options]);

    const variants = product.variants.nodes.map((variant) => variant);

    const selectedVariant = variants.find((variant) => {
      return selectedOptions?.every((selectedOption) =>
        variant.selectedOptions.some(
          (option) =>
            option.name === selectedOption.name &&
            option.value === selectedOption.value,
        ),
      );
    });

    const selectedOption = (option: string) =>
      selectedOptions?.find((selectedOption) => selectedOption.name === option);

    return (
      <div aria-label="Upsell product">
        <hr className={trackingCss.productDivider} />

        <Flex align="center" className={trackingCss.product} key={index}>
          <img
            // @ts-expect-error: strict-null-checks
            alt={getAltText(product)}
            className={trackingCss.productImage}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            // @ts-expect-error: strict-null-checks
            src={getImageUrl(product)}
            style={{
              aspectRatio: imageAspectRatio ?? ProductImageAspectRatio.SQUARE,
              height: "auto",
            }}
          />
          <Flex
            className={trackingCss.productInfo}
            dir="row"
            grow={1}
            justify="space-between"
          >
            <Flex dir="column" flex={1} gap="xs">
              <Text
                className={classNames(
                  trackingCss.thin,
                  trackingCss.darkGrey,
                  trackingCss.productTitle,
                )}
                fontSize="xs"
              >
                {product.title}
              </Text>
              <span aria-label="Product price">
                {getPriceDetails(
                  product,
                  collectionsLoad,
                  trackable,
                  formatCurrency,
                  discountResponse?.settings,
                )}
              </span>
              {settings.orderTracking?.upsellProducts
                ?.productEstimateEnabled && (
                <ProductDeliveryRange
                  shippingAddress={
                    trackable.originalOrder.shopify.shippingAddress
                  }
                />
              )}
              {Object.keys(options).map((option, index) => (
                <Flex key={index}>
                  <RedoSingleSelectDropdownInput
                    fitToAnchor={false}
                    flexGrow={false}
                    options={
                      // @ts-expect-error: strict-null-checks
                      options[option].map((option) => ({
                        value: option,
                        id: option,
                        type: "text",
                        text: option,
                      }))
                    }
                    optionSelected={({ item: selectedOption }) => {
                      setSelectedOptions((prevOptions) => {
                        const existingOption = prevOptions?.find(
                          (prevOption) => prevOption.name === option,
                        );
                        if (existingOption) {
                          // @ts-expect-error: strict-null-checks
                          return prevOptions.map((prevOption) =>
                            prevOption.name === option
                              ? { name: option, value: selectedOption.value }
                              : prevOption,
                          );
                        }
                        return [
                          ...(prevOptions || []),
                          { name: option, value: selectedOption.value },
                        ];
                      });
                    }}
                    selectedItem={
                      selectedOption(option)
                        ? {
                            // @ts-expect-error: strict-null-checks
                            value: selectedOption(option).value,
                            id:
                              // @ts-expect-error: strict-null-checks
                              selectedOption(option).value,
                            type: "text",
                            text:
                              // @ts-expect-error: strict-null-checks
                              selectedOption(option).value,
                          }
                        : {
                            value: option,
                            id: option,
                            type: "text",
                            text: option,
                          }
                    }
                    size="xs"
                  />
                </Flex>
              ))}
            </Flex>
            <a
              onClick={async () =>
                await upsellRedirect.logUpsellAndRedirect({
                  id: selectedVariant?.id?.split("/").pop() ?? "",
                  handle: product.handle,
                })
              }
              rel="noreferrer"
              target="_blank"
            >
              <RedoButton
                hierarchy="secondary"
                size="sm"
                text="Add to cart"
                theme="normal"
              />
            </a>
          </Flex>
        </Flex>
      </div>
    );
  },
);

const getPriceDetails = (
  product: any,
  collectionsLoad: any,
  trackable: Trackable,
  formatCurrency: (amount: number) => string,
  discountSettings?: DiscountResponse["settings"],
) => {
  const defaultPriceDisplay = (
    <Text fontWeight="semibold">{formatCurrency(product.price)}</Text>
  );

  // Explicitly checking discountSettings as validDiscount requires non-nullable input
  if (!discountSettings || !validDiscount(discountSettings, trackable)) {
    return defaultPriceDisplay;
  }

  if (discountSettings?.settings?.discountType === DiscountType.FREE_SHIPPING) {
    return defaultPriceDisplay;
  } else if (
    discountSettings?.settings?.discountType === DiscountType.ORDER ||
    discountSettings?.settings?.discountType === DiscountType.PRODUCT
  ) {
    const { price, discounted } = calculateDiscountPrice(
      product,
      discountSettings?.settings,
      collectionsLoad,
    );
    return (
      <span>
        {discounted && (
          <span
            className={classNames(trackingCss.strikethrough, trackingCss.grey)}
          >
            {formatCurrency(product.price)}
          </span>
        )}
        <Text fontWeight="semibold">{formatCurrency(price)}</Text>
      </span>
    );
  }

  return defaultPriceDisplay;
};
